import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, firstValueFrom } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthorizedApiService, CanAccessDirective } from '@core';
import { AuthorizationService } from '@core/service/authorization.service';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { ConfirmDeleteDialogComponent } from '@shared/components/confirm-delete-dialog/confirm-delete-dialog.component';
import { DateTimePickerFieldComponent } from '@shared/components/date-time-picker-field/date-time-picker-field.component';
import { LoadingStateComponent } from '@shared/components/loading-state/loading-state.component';
import { FirebaseStorageService } from '@shared/services/firebase-storage.service';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { environment } from '../../../environments/environment';
import { resolveApiUrl } from '../../../environments/resolve-api-url';
import { CompaniesRecord } from '../../organization/companies-records/companies-record.interface';
import { EmployeeRecord } from '../../organization/employees/employee.interface';
import { UserConfig } from '../../config/users/user.interface';
import { AttendanceJustificationTypeRecord } from '../attendance-justification-type/attendance-justification-type.interface';
import {
  AttendanceJustificationAttachment,
  AttendanceJustificationFormValue,
  AttendanceJustificationPayload,
  AttendanceJustificationRecord,
  AttendanceJustificationResolutionPayload,
  AttendanceJustificationScope,
} from './attendance-justification.interface';
import {
  AttendanceJustificationResolutionDialogComponent,
  AttendanceJustificationResolutionDialogData,
} from './attendance-justification-resolution-dialog.component';

type AttendanceJustificationMode = 'view' | 'create' | 'edit';

interface CompaniesResponse {
  data?: CompaniesRecord[];
  items?: CompaniesRecord[];
  results?: CompaniesRecord[];
  companiesRecords?: CompaniesRecord[];
}

interface OrgUnitRecord {
  orgUnitId: string;
  companiesRecordId: string;
  name: string;
  parentId?: string | null;
  isActive?: boolean;
}

interface OrgUnitsResponse {
  data?: OrgUnitRecord[];
  items?: OrgUnitRecord[];
  results?: OrgUnitRecord[];
  orgUnits?: OrgUnitRecord[];
}

interface EmployeesResponse {
  data?: EmployeeRecord[];
  items?: EmployeeRecord[];
  results?: EmployeeRecord[];
  employees?: EmployeeRecord[];
}

@Component({
  selector: 'app-attendance-justification-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    BreadcrumbComponent,
    LoadingStateComponent,
    DateTimePickerFieldComponent,
    CanAccessDirective,
    MatButtonModule,
    MatAutocompleteModule,
    MatCardModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatNativeDateModule,
    MatSelectModule,
    TranslateModule,
  ],
  templateUrl: './attendance-justification-detail.component.html',
  styleUrl: './attendance-justification-detail.component.scss',
})
export class AttendanceJustificationDetailComponent implements OnInit {
  private readonly apiService = inject(AuthorizedApiService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly firebaseStorageService = inject(FirebaseStorageService);

  isLoading = true;
  isSaving = false;
  isDeleting = false;
  isResolving = false;
  isUploadingAttachments = false;
  isAbsenceFlow = false;
  mode: AttendanceJustificationMode = 'view';
  selectedAttendanceJustificationId: string | null = null;
  selectedAttendanceJustification: AttendanceJustificationRecord | null = null;
  companies: CompaniesRecord[] = [];
  orgUnits: OrgUnitRecord[] = [];
  employees: EmployeeRecord[] = [];
  justificationTypes: AttendanceJustificationTypeRecord[] = [];
  form: AttendanceJustificationFormValue = this.createEmptyForm();
  employeeSearchText: string | EmployeeRecord = '';
  attachmentItems: AttendanceJustificationAttachment[] = [];
  selectedAttachmentFiles: File[] = [];
  openingAttachmentIndexes = new Set<number>();
  private readonly attachmentObjectUrls = new Set<string>();
  private readonly attachmentPreviewUrls = new Map<number, string>();
  private readonly apiBaseUrl = resolveApiUrl(environment.apiUrl);
  private employeeSearchDebounceHandle: ReturnType<typeof setTimeout> | null = null;

  readonly readAccess = { resource: 'attendanceJustifications', action: 'read' } as const;
  readonly createAccess = { resource: 'attendanceJustifications', action: 'create' } as const;
  readonly updateAccess = { resource: 'attendanceJustifications', action: 'update' } as const;
  readonly deleteAccess = { resource: 'attendanceJustifications', action: 'delete' } as const;
  readonly scopeOptions: AttendanceJustificationScope[] = ['full_day', 'partial_minutes'];

  ngOnInit(): void {
    this.destroyRef.onDestroy(() => {
      this.attachmentObjectUrls.forEach((url) => URL.revokeObjectURL(url));
      this.attachmentObjectUrls.clear();
    });
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.selectedAttendanceJustification) {
        this.selectedAttendanceJustification = this.normalizeRecord(this.selectedAttendanceJustification);
      }
    });

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        void this.syncWithRoute();
      });

    void this.syncWithRoute();
  }

  get isCreateMode(): boolean {
    return this.mode === 'create';
  }

  get isEditMode(): boolean {
    return this.mode === 'edit';
  }

  get isViewMode(): boolean {
    return this.mode === 'view';
  }

  get canSave(): boolean {
    return !this.validateForm();
  }

  get selectedTypeConfig(): AttendanceJustificationTypeRecord | null {
    const typeId = this.form.attendanceJustificationTypeId || this.selectedAttendanceJustification?.attendanceJustificationTypeId;
    if (!typeId) {
      return null;
    }
    return this.justificationTypes.find((item) => item.attendanceJustificationTypeId === typeId) || null;
  }

  get availableScopeOptions(): AttendanceJustificationScope[] {
    if (this.isAbsenceFlow) {
      return ['full_day'];
    }
    const selectedType = this.selectedTypeConfig;
    if (!selectedType) {
      return this.scopeOptions;
    }
    if (selectedType.scopeAllowed === 'both') {
      return this.scopeOptions;
    }
    return [selectedType.scopeAllowed];
  }

  get showMinutesField(): boolean {
    return this.form.scope === 'partial_minutes';
  }

  get detailTitle(): string {
    if (this.isCreateMode) {
      return this.t('attendanceJustificationsCatalog.detail.createTitle');
    }
    if (this.isEditMode) {
      return this.t('attendanceJustificationsCatalog.detail.editTitle');
    }
    return this.selectedAttendanceJustification?.typeLabel || this.t('attendanceJustificationsCatalog.detail.title');
  }

  get detailDescription(): string {
    if (this.isCreateMode) {
      return this.t('attendanceJustificationsCatalog.detail.createDescription');
    }
    if (this.isEditMode) {
      return this.t('attendanceJustificationsCatalog.detail.editDescription');
    }
    return this.t('attendanceJustificationsCatalog.detail.description');
  }

  get workDayLabel(): string {
    const value = this.selectedAttendanceJustification?.workDate;
    if (!value) {
      return '-';
    }
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) {
      return '-';
    }
    const language = this.translate.currentLang || this.translate.defaultLang || 'es';
    const locale = language.startsWith('de') ? 'de-DE' : language.startsWith('en') || language.startsWith('us') ? 'en-US' : 'es-MX';
    const weekday = new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(date);
    return weekday.charAt(0).toUpperCase() + weekday.slice(1);
  }

  get attachmentsPreview(): AttendanceJustificationAttachment[] {
    return this.attachmentItems;
  }

  get filteredEmployees(): EmployeeRecord[] {
    return this.employees;
  }

  getAttachmentPreviewUrl(attachmentIndex: number): string {
    return this.attachmentPreviewUrls.get(this.getAttachmentBackendIndex(attachmentIndex)) || '';
  }

  async viewAttachment(attachmentIndex: number): Promise<void> {
    const justificationId = this.selectedAttendanceJustificationId;
    if (!justificationId || this.openingAttachmentIndexes.has(attachmentIndex)) {
      return;
    }

    const previewWindow = window.open('', '_blank');
    this.openingAttachmentIndexes.add(attachmentIndex);
    try {
      const attachment = this.attachmentItems[attachmentIndex];
      const backendIndex = this.getAttachmentBackendIndex(attachmentIndex);
      const viewUrl = this.resolveAttachmentViewUrl(
        attachment?.viewUrl,
        justificationId,
        backendIndex
      );
      const response = await firstValueFrom(this.apiService.getBlob(viewUrl));
      const blob = response instanceof Blob ? response : response.body;
      if (!(blob instanceof Blob) || blob.size === 0) {
        throw new Error('Empty attachment response');
      }

      const objectUrl = URL.createObjectURL(blob);
      this.attachmentObjectUrls.add(objectUrl);
      if (previewWindow) {
        previewWindow.location.href = objectUrl;
      } else {
        const link = document.createElement('a');
        link.href = objectUrl;
        link.target = '_blank';
        link.rel = 'noopener';
        link.click();
      }
    } catch (error) {
      previewWindow?.close();
      console.error('Error loading attendance justification attachment', error);
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('attendanceJustificationsCatalog.notifications.attachmentViewError')),
        'bottom',
        'center'
      );
    } finally {
      this.openingAttachmentIndexes.delete(attachmentIndex);
    }
  }

  readonly displayEmployeeOption = (value: string | EmployeeRecord | null): string => this.getEmployeeSearchText(value);

  get selectedEmployeeRecord(): EmployeeRecord | null {
    if (!this.form.employeeId) {
      return null;
    }
    return this.employees.find((employee) => employee.employeeId === this.form.employeeId) || null;
  }

  get selectedEmployeeOrgUnitLabel(): string {
    const employee = this.selectedEmployeeRecord;
    if (!employee?.orgUnitId) {
      return this.t('attendanceJustificationsCatalog.labels.noOrgUnit');
    }

    return (
      employee.orgUnitName ||
      this.orgUnits.find((orgUnit) => orgUnit.orgUnitId === employee.orgUnitId)?.name ||
      this.t('attendanceJustificationsCatalog.labels.noOrgUnit')
    );
  }

  async startCreate(): Promise<void> {
    if (!this.hasAccess('create')) {
      return;
    }
    await this.router.navigate(['/attendance/attendance-justifications/new']);
  }

  async startEdit(): Promise<void> {
    if (!this.selectedAttendanceJustificationId || !this.hasAccess('update') || this.selectedAttendanceJustification?.status !== 'submitted') {
      return;
    }
    await this.router.navigate(['/attendance/attendance-justifications', this.selectedAttendanceJustificationId, 'edit']);
  }

  openDatePicker(input: HTMLInputElement): void {
    const pickerInput = input as HTMLInputElement & { showPicker?: () => void };
    if (typeof pickerInput.showPicker === 'function') {
      pickerInput.showPicker();
      return;
    }

    input.focus();
    input.click();
  }

  toPickerDate(value?: string | null): Date | null {
    if (!value) {
      return null;
    }

    const [year, month, day] = value.split('-').map((part) => Number(part));
    if (!year || !month || !day) {
      return null;
    }

    return new Date(year, month - 1, day);
  }

  fromPickerDate(value: Date | null | undefined): string {
    if (!value || Number.isNaN(value.getTime())) {
      return '';
    }

    const year = value.getFullYear();
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  onWorkDateSelected(event: MatDatepickerInputEvent<Date>): void {
    this.form.workDate = this.fromPickerDate(event.value);
  }

  async cancelEdit(): Promise<void> {
    if (this.selectedAttendanceJustificationId) {
      await this.router.navigate(['/attendance/attendance-justifications', this.selectedAttendanceJustificationId]);
      return;
    }
    await this.router.navigate(['/attendance/attendance-justifications']);
  }

  async goBackToList(): Promise<void> {
    await this.router.navigate(['/attendance/attendance-justifications']);
  }

  onEmployeeSearchInput(value: string | EmployeeRecord): void {
    const searchText = this.getEmployeeSearchText(value);
    this.employeeSearchText = value;
    const selectedEmployee = this.selectedEmployeeRecord;
    if (selectedEmployee && this.formatEmployeeOptionLabel(selectedEmployee) !== searchText.trim()) {
      this.form.employeeId = '';
    }

    if (this.employeeSearchDebounceHandle) {
      clearTimeout(this.employeeSearchDebounceHandle);
    }

    this.employeeSearchDebounceHandle = setTimeout(() => {
      void this.loadEmployees(searchText.trim());
    }, 250);
  }

  onEmployeeSelected(event: MatAutocompleteSelectedEvent): void {
    const employee = event.option.value as EmployeeRecord | null;
    const employeeId = employee?.employeeId || '';
    this.form.employeeId = employeeId;
    this.form.companiesRecordId = employee?.companiesRecordId || '';
    this.form.orgUnitId = employee?.orgUnitId || '';
    this.employeeSearchText = employee || '';
  }

  onTypeChanged(): void {
    const allowedScopes = this.availableScopeOptions;
    if (!allowedScopes.includes(this.form.scope)) {
      this.form.scope = allowedScopes[0] || 'full_day';
    }
    if (this.form.scope !== 'partial_minutes') {
      this.form.minutesText = '';
    }
  }

  onAttachmentFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const files = Array.from(input?.files || []).filter((file) => (file.type || '').startsWith('image/'));
    this.selectedAttachmentFiles = files;
    if (input) {
      input.value = '';
    }
  }

  removeAttachmentAt(index: number): void {
    if (index < 0 || index >= this.attachmentItems.length) {
      return;
    }
    this.attachmentItems = this.attachmentItems.filter((_, currentIndex) => currentIndex !== index);
  }

  async save(): Promise<void> {
    const validationMessage = this.validateForm();
    if (validationMessage) {
      this.showNotification('snackbar-warning', validationMessage, 'bottom', 'center');
      return;
    }

    this.isSaving = true;
    try {
      const payload = await this.buildPayload();
      if (this.isCreateMode) {
        const created = await firstValueFrom(this.apiService.post<AttendanceJustificationRecord>('attendance-justifications', payload));
        this.showNotification('snackbar-success', this.t('attendanceJustificationsCatalog.notifications.created'), 'bottom', 'center');
        await this.router.navigate(['/attendance/attendance-justifications', created.attendanceJustificationId]);
      } else if (this.isEditMode && this.selectedAttendanceJustificationId) {
        await firstValueFrom(
          this.apiService.patch<AttendanceJustificationRecord>(
            `attendance-justifications/${this.selectedAttendanceJustificationId}`,
            payload
          )
        );
        this.showNotification('snackbar-success', this.t('attendanceJustificationsCatalog.notifications.updated'), 'bottom', 'center');
        await this.router.navigate(['/attendance/attendance-justifications', this.selectedAttendanceJustificationId]);
      }
    } catch (error) {
      console.error('Error saving attendance justification', error);
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('attendanceJustificationsCatalog.notifications.saveError')),
        'bottom',
        'center'
      );
    } finally {
      this.isSaving = false;
    }
  }

  deleteSelected(): void {
    if (!this.selectedAttendanceJustification || !this.selectedAttendanceJustificationId || !this.hasAccess('delete')) {
      return;
    }
    if (this.selectedAttendanceJustification.status === 'approved') {
      this.showNotification('snackbar-warning', this.t('attendanceJustificationsCatalog.notifications.deleteApprovedBlocked'), 'bottom', 'center');
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        title: this.t('attendanceJustificationsCatalog.dialog.deleteTitle'),
        message: this.t('attendanceJustificationsCatalog.dialog.deleteMessage', {
          justification: this.selectedAttendanceJustification.typeLabel,
        }),
        confirmLabel: this.t('attendanceJustificationsCatalog.dialog.deleteConfirm'),
        cancelLabel: this.t('common.cancel'),
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed || !this.selectedAttendanceJustificationId) {
        return;
      }

      this.isDeleting = true;
      try {
        await firstValueFrom(this.apiService.delete(`attendance-justifications/${this.selectedAttendanceJustificationId}`));
        this.showNotification('snackbar-success', this.t('attendanceJustificationsCatalog.notifications.deleted'), 'bottom', 'center');
        await this.router.navigate(['/attendance/attendance-justifications']);
      } catch (error) {
        console.error('Error deleting attendance justification', error);
        this.showNotification(
          'snackbar-danger',
          extractApiErrorMessage(error, this.t('attendanceJustificationsCatalog.notifications.deleteError')),
          'bottom',
          'center'
        );
      } finally {
        this.isDeleting = false;
      }
    });
  }

  approveSelected(): void {
    void this.resolveSelected('approve');
  }

  rejectSelected(): void {
    void this.resolveSelected('reject');
  }

  cancelSelected(): void {
    void this.resolveSelected('cancel');
  }

  private async resolveSelected(action: 'approve' | 'reject' | 'cancel'): Promise<void> {
    if (!this.selectedAttendanceJustificationId || !this.selectedAttendanceJustification || !this.hasAccess('update')) {
      return;
    }

    const dialogConfig: Record<'approve' | 'reject' | 'cancel', AttendanceJustificationResolutionDialogData> = {
      approve: {
        title: this.t('attendanceJustificationsCatalog.dialog.approveTitle'),
        message: this.t('attendanceJustificationsCatalog.dialog.approveMessage', { justification: this.selectedAttendanceJustification.typeLabel }),
        commentLabel: this.t('attendanceJustificationsCatalog.fields.resolutionComment'),
        commentPlaceholder: this.t('attendanceJustificationsCatalog.fields.resolutionCommentPlaceholder'),
        confirmLabel: this.t('attendanceJustificationsCatalog.actions.approve'),
        cancelLabel: this.t('common.cancel'),
      },
      reject: {
        title: this.t('attendanceJustificationsCatalog.dialog.rejectTitle'),
        message: this.t('attendanceJustificationsCatalog.dialog.rejectMessage', { justification: this.selectedAttendanceJustification.typeLabel }),
        commentLabel: this.t('attendanceJustificationsCatalog.fields.resolutionComment'),
        commentPlaceholder: this.t('attendanceJustificationsCatalog.fields.resolutionCommentPlaceholder'),
        confirmLabel: this.t('attendanceJustificationsCatalog.actions.reject'),
        cancelLabel: this.t('common.cancel'),
        required: true,
      },
      cancel: {
        title: this.t('attendanceJustificationsCatalog.dialog.cancelTitle'),
        message: this.t('attendanceJustificationsCatalog.dialog.cancelMessage', { justification: this.selectedAttendanceJustification.typeLabel }),
        commentLabel: this.t('attendanceJustificationsCatalog.fields.resolutionComment'),
        commentPlaceholder: this.t('attendanceJustificationsCatalog.fields.resolutionCommentPlaceholder'),
        confirmLabel: this.t('attendanceJustificationsCatalog.actions.cancel'),
        cancelLabel: this.t('common.cancel'),
      },
    };

    const dialogRef = this.dialog.open(AttendanceJustificationResolutionDialogComponent, {
      width: '520px',
      data: dialogConfig[action],
    });

    const comment = await firstValueFrom(dialogRef.afterClosed());
    if (comment === undefined || comment === null) {
      return;
    }

    this.isResolving = true;
    try {
      const payload: AttendanceJustificationResolutionPayload = { comment: comment || null };
      await firstValueFrom(this.apiService.post(`attendance-justifications/${this.selectedAttendanceJustificationId}/${action}`, payload));
      this.showNotification(
        'snackbar-success',
        this.t(`attendanceJustificationsCatalog.notifications.${action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : 'cancelled'}`),
        'bottom',
        'center'
      );
      await this.loadAttendanceJustification(this.selectedAttendanceJustificationId);
      this.mode = 'view';
    } catch (error) {
      console.error(`Error resolving attendance justification with action ${action}`, error);
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('attendanceJustificationsCatalog.notifications.resolutionError')),
        'bottom',
        'center'
      );
    } finally {
      this.isResolving = false;
    }
  }

  private async syncWithRoute(): Promise<void> {
    this.isLoading = true;
    await this.loadCompanies();
    await this.loadJustificationTypes();

    const attendanceJustificationId = this.route.snapshot.paramMap.get('attendanceJustificationId');
    const currentUrl = this.router.url.split('?')[0];
    const isCreateRoute = currentUrl.endsWith('/attendance/attendance-justifications/new');
    const isEditRoute = /\/attendance\/attendance-justifications\/[^/]+\/edit$/.test(currentUrl);

    if (isCreateRoute) {
      this.isAbsenceFlow = this.route.snapshot.queryParamMap.get('source') === 'absences';
      this.mode = 'create';
      this.selectedAttendanceJustificationId = null;
      this.selectedAttendanceJustification = null;
      this.form = this.createEmptyForm();
      this.employeeSearchText = '';
      this.attachmentItems = [];
      this.selectedAttachmentFiles = [];
      await this.loadOrgUnits();
      await this.loadEmployees();
      await this.applyCreateRouteDefaults();
      this.isLoading = false;
      return;
    }

    this.isAbsenceFlow = false;

    if (!attendanceJustificationId) {
      this.mode = 'view';
      this.selectedAttendanceJustificationId = null;
      this.selectedAttendanceJustification = null;
      this.employeeSearchText = '';
      this.attachmentItems = [];
      this.selectedAttachmentFiles = [];
      this.isLoading = false;
      return;
    }

    await this.loadAttendanceJustification(attendanceJustificationId);
    this.mode = isEditRoute ? 'edit' : 'view';
    this.isLoading = false;
  }

  private async loadAttendanceJustification(attendanceJustificationId: string): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.apiService.get<AttendanceJustificationRecord>(`attendance-justifications/${attendanceJustificationId}`)
      );
      this.selectedAttendanceJustificationId = response.attendanceJustificationId;
      this.form = this.createFormFromRecord(response);
      await this.loadOrgUnits();
      await this.loadEmployees();
      await this.ensureRelatedRecordsLoaded(response);
      this.releaseAttachmentPreviewUrls();
      this.attachmentItems = Array.isArray(response.attachments)
        ? response.attachments.map((attachment, attachmentIndex) => ({
            ...attachment,
            _backendIndex: attachmentIndex,
          }))
        : [];
      this.selectedAttachmentFiles = [];
      void this.loadAttachmentPreviews(response.attendanceJustificationId);
      this.selectedAttendanceJustification = this.normalizeRecord(response);
      await this.resolveActorNames();
      this.employeeSearchText = this.selectedEmployeeRecord || '';

      const typeExists = this.justificationTypes.some((type) => type.attendanceJustificationTypeId === response.attendanceJustificationTypeId);
      if (!typeExists && response.attendanceJustificationTypeId) {
        this.justificationTypes = [
          ...this.justificationTypes,
          {
            attendanceJustificationTypeId: response.attendanceJustificationTypeId,
            center: response.center,
            code: response.typeCode,
            label: response.typeLabel,
            description: '',
            scopeAllowed: response.scope,
            requiresApproval: response.status !== 'approved',
            payEffect: response.payEffect,
            attendanceEffect: response.attendanceEffect,
            requireComment: false,
            requireAttachment: false,
            allowEmployeeCreate: true,
            allowAdminCreate: true,
            isActive: response.isActive,
          },
        ];
      }
    } catch (error) {
      console.error('Error loading attendance justification detail', error);
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('attendanceJustificationsCatalog.notifications.loadDetailError')),
        'bottom',
        'center'
      );
    }
  }

  private createEmptyForm(): AttendanceJustificationFormValue {
    return {
      companiesRecordId: '',
      orgUnitId: '',
      employeeId: '',
      attendanceJustificationTypeId: '',
      scope: 'full_day',
      workDate: '',
      minutesText: '',
      comment: '',
      isActive: true,
    };
  }

  private createFormFromRecord(record: AttendanceJustificationRecord): AttendanceJustificationFormValue {
    return {
      companiesRecordId: record.companiesRecordId || '',
      orgUnitId: record.orgUnitId || '',
      employeeId: record.employeeId || '',
      attendanceJustificationTypeId: record.attendanceJustificationTypeId || '',
      scope: record.scope || 'full_day',
      workDate: record.workDate || '',
      minutesText: record.minutes !== null && record.minutes !== undefined ? String(record.minutes) : '',
      comment: record.comment || '',
      isActive: record.isActive !== false,
    };
  }

  private async buildPayload(): Promise<AttendanceJustificationPayload> {
    const payload: AttendanceJustificationPayload = {
      employeeId: this.form.employeeId,
      attendanceJustificationTypeId: this.form.attendanceJustificationTypeId,
      scope: this.form.scope,
      workDate: this.form.workDate,
      comment: this.form.comment.trim() || null,
      attachments: await this.resolveAttachmentsForSave(),
    };

    if (this.form.scope === 'partial_minutes') {
      payload.minutes = Number(this.form.minutesText);
    }

    return payload;
  }

  private validateForm(): string | null {
    if (!this.form.employeeId) {
      return this.t('attendanceJustificationsCatalog.notifications.validationEmployee');
    }
    if (!this.form.attendanceJustificationTypeId) {
      return this.t('attendanceJustificationsCatalog.notifications.validationType');
    }
    if (!this.form.scope) {
      return this.t('attendanceJustificationsCatalog.notifications.validationScope');
    }
    if (!this.form.workDate) {
      return this.t('attendanceJustificationsCatalog.notifications.validationWorkDate');
    }

    const selectedType = this.selectedTypeConfig;
    if (this.form.scope === 'partial_minutes') {
      const minutes = Number(this.form.minutesText);
      if (!Number.isFinite(minutes) || minutes <= 0) {
        return this.t('attendanceJustificationsCatalog.notifications.validationMinutes');
      }
    }

    if (selectedType?.requireComment && !this.form.comment.trim()) {
      return this.t('attendanceJustificationsCatalog.notifications.validationCommentRequired');
    }

    if (selectedType?.requireAttachment && !this.attachmentItems.length && !this.selectedAttachmentFiles.length) {
      return this.t('attendanceJustificationsCatalog.notifications.validationAttachmentRequired');
    }

    return null;
  }

  private async resolveAttachmentsForSave(): Promise<AttendanceJustificationAttachment[]> {
    if (!this.selectedAttachmentFiles.length) {
      return this.attachmentItems.map((attachment) => this.toAttachmentPayload(attachment));
    }

    this.isUploadingAttachments = true;
    try {
      const employeeId = this.form.employeeId || this.selectedAttendanceJustification?.employeeId || 'general';
      const uploaded = await Promise.all(
        this.selectedAttachmentFiles.map(async (file) => {
          const upload = await this.firebaseStorageService.uploadAttendanceJustificationAttachment(file, employeeId);
          return {
            url: upload.url,
            storagePath: upload.storagePath,
            name: file.name,
            contentType: file.type || null,
            size: file.size,
            uploadedAt: new Date().toISOString(),
          } satisfies AttendanceJustificationAttachment;
        })
      );

      this.selectedAttachmentFiles = [];
      this.attachmentItems = [...this.attachmentItems, ...uploaded];
      return this.attachmentItems.map((attachment) => this.toAttachmentPayload(attachment));
    } finally {
      this.isUploadingAttachments = false;
    }
  }

  private toAttachmentPayload(attachment: AttendanceJustificationAttachment): AttendanceJustificationAttachment {
    const { _backendIndex, ...payload } = attachment;
    return payload;
  }

  private async loadCompanies(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.apiService.get<CompaniesRecord[] | CompaniesResponse>('companies-records', { params: { isActive: 'true' } })
      );
      this.companies = this.extractCompanies(response)
        .filter((company) => company.isActive !== false)
        .sort((left, right) => left.name.localeCompare(right.name));
    } catch (error) {
      console.error('Error loading companies for attendance justifications detail', error);
      this.companies = [];
    }
  }

  private async loadJustificationTypes(): Promise<void> {
    try {
      const response = await firstValueFrom(this.apiService.get<AttendanceJustificationTypeRecord[]>('attendance-justification-types'));
      this.justificationTypes = (response || [])
        .filter((item) => item.isActive !== false)
        .sort((left, right) => left.label.localeCompare(right.label));
    } catch (error) {
      console.error('Error loading justification types for attendance justification detail', error);
      this.justificationTypes = [];
    }
  }

  private async applyCreateRouteDefaults(): Promise<void> {
    if (!this.isAbsenceFlow) {
      return;
    }

    const employeeId = this.route.snapshot.queryParamMap.get('employeeId') || '';
    const workDate = this.route.snapshot.queryParamMap.get('workDate') || '';
    this.justificationTypes = this.justificationTypes.filter((type) => type.attendanceEffect === 'justify_day');
    this.form.scope = 'full_day';
    this.form.workDate = workDate;

    if (employeeId && !this.employees.some((employee) => employee.employeeId === employeeId)) {
      try {
        const employeeResponse = await firstValueFrom(
          this.apiService.get<{ entity?: EmployeeRecord }>(`employees/${employeeId}/with-operational-config`)
        );
        const requestedEmployee = employeeResponse?.entity;
        if (requestedEmployee?.employeeId) {
          this.employees = [requestedEmployee, ...this.employees];
        }
      } catch (error) {
        console.warn('Could not preload the absence employee by id', error);
      }
    }

    const employee = this.employees.find((item) => item.employeeId === employeeId);
    if (employee) {
      this.form.employeeId = employee.employeeId;
      this.form.companiesRecordId = employee.companiesRecordId || '';
      this.form.orgUnitId = employee.orgUnitId || '';
      this.employeeSearchText = employee;
    }
  }

  private async loadOrgUnits(): Promise<void> {
    try {
      const response = await firstValueFrom(this.apiService.get<OrgUnitRecord[] | OrgUnitsResponse>('org-units'));
      const companyUnits = this.extractOrgUnits(response)
        .filter((unit) => unit.isActive !== false);
      const parentIds = new Set(companyUnits.map((item) => item.parentId || '').filter(Boolean));
      this.orgUnits = companyUnits
        .filter((unit) => !parentIds.has(unit.orgUnitId))
        .sort((left, right) => left.name.localeCompare(right.name));
    } catch (error) {
      console.error('Error loading org units for attendance justification detail', error);
      this.orgUnits = [];
    }
  }

  private async loadEmployees(search = ''): Promise<void> {
    try {
      const selectedEmployee = this.selectedEmployeeRecord;
      const response = await firstValueFrom(
        this.apiService.get<EmployeeRecord[] | EmployeesResponse>('employees', {
          params: { page: 1, perPage: 20, isActive: 'true', ...(search ? { search } : {}) },
        })
      );
      const nextEmployees = this.extractEmployees(response)
        .filter((employee) => employee.isActive !== false)
        .sort((left, right) => (left.fullName || '').localeCompare(right.fullName || ''));

      this.employees = selectedEmployee && !nextEmployees.some((employee) => employee.employeeId === selectedEmployee.employeeId)
        ? [selectedEmployee, ...nextEmployees]
        : nextEmployees;

      if (this.form.employeeId) {
        const refreshedSelectedEmployee = this.selectedEmployeeRecord;
        this.employeeSearchText = refreshedSelectedEmployee || this.getEmployeeSearchText(this.employeeSearchText);
      }
    } catch (error) {
      console.error('Error loading employees for attendance justification detail', error);
      this.employees = [];
    }
  }

  private async ensureRelatedRecordsLoaded(record: AttendanceJustificationRecord): Promise<void> {
    const requests: Promise<void>[] = [];

    if (record.employeeId && !this.employees.some((employee) => employee.employeeId === record.employeeId)) {
      requests.push((async () => {
        try {
          const response = await firstValueFrom(
            this.apiService.get<{ entity?: EmployeeRecord }>(`employees/${record.employeeId}/with-operational-config`)
          );
          if (response.entity?.employeeId) {
            this.employees = [response.entity, ...this.employees];
          }
        } catch (error) {
          console.warn('Could not resolve attendance justification employee', error);
        }
      })());
    }

    if (record.orgUnitId && !this.orgUnits.some((unit) => unit.orgUnitId === record.orgUnitId)) {
      requests.push((async () => {
        try {
          const orgUnit = await firstValueFrom(
            this.apiService.get<OrgUnitRecord>(`org-units/${record.orgUnitId}`)
          );
          if (orgUnit?.orgUnitId) {
            this.orgUnits = [orgUnit, ...this.orgUnits];
          }
        } catch (error) {
          console.warn('Could not resolve attendance justification organizational unit', error);
        }
      })());
    }

    await Promise.all(requests);
  }

  private extractCompanies(response: CompaniesRecord[] | CompaniesResponse): CompaniesRecord[] {
    if (Array.isArray(response)) {
      return response;
    }
    return response.items || response.data || response.results || response.companiesRecords || [];
  }

  private extractOrgUnits(response: OrgUnitRecord[] | OrgUnitsResponse): OrgUnitRecord[] {
    if (Array.isArray(response)) {
      return response;
    }
    return response.items || response.data || response.results || response.orgUnits || [];
  }

  private extractEmployees(response: EmployeeRecord[] | EmployeesResponse): EmployeeRecord[] {
    if (Array.isArray(response)) {
      return response;
    }
    return response.items || response.data || response.results || response.employees || [];
  }

  private normalizeRecord(record: AttendanceJustificationRecord): AttendanceJustificationRecord {
    const employee = this.employees.find((item) => item.employeeId === record.employeeId);
    return {
      ...record,
      employeeNumber: record.employeeNumber || employee?.employeeNumber || '-',
      employeeLabel: employee?.fullName || record.employeeLabel || record.employeeId || '-',
      companyName:
        this.companies.find((item) => item.companiesRecordId === record.companiesRecordId)?.name ||
        this.t('attendanceJustificationsCatalog.labels.noCompany'),
      orgUnitName:
        this.orgUnits.find((item) => item.orgUnitId === record.orgUnitId)?.name ||
        (record.orgUnitId ? record.orgUnitId : this.t('attendanceJustificationsCatalog.labels.noOrgUnit')),
      scopeLabel: this.t(`attendanceJustificationsCatalog.scopeOptions.${record.scope}`),
      statusLabel: this.t(`attendanceJustificationsCatalog.statuses.${record.status}`),
      payEffectLabel: this.t(`attendanceJustificationsCatalog.payEffectOptions.${record.payEffect}`),
      attendanceEffectLabel: this.t(`attendanceJustificationsCatalog.attendanceEffectOptions.${record.attendanceEffect}`),
      activeLabel: record.isActive ? this.t('common.status.active') : this.t('common.status.inactive'),
      minutesLabel: record.minutes === null || record.minutes === undefined ? '-' : `${record.minutes} min`,
      workDateLabel: this.formatDate(record.workDate),
      submittedAtLabel: this.formatDateTime(record.submittedAt),
      resolvedAtLabel: this.formatDateTime(record.resolvedAt),
      createdAtLabel: this.formatDateTime(record.createdAt),
      updatedAtLabel: this.formatDateTime(record.updatedAt),
      resolutionTypeLabel: record.resolutionType ? this.t(`attendanceJustificationsCatalog.resolutionTypes.${record.resolutionType}`) : '-',
    };
  }

  private async resolveActorNames(): Promise<void> {
    const justification = this.selectedAttendanceJustification;
    if (!justification) {
      return;
    }

    const actorUids = [
      justification.submittedByUid,
      justification.resolvedByUid,
      ...(justification.history || []).map((item) => item.byUid),
    ]
      .filter((uid): uid is string => !!uid);
    if (!actorUids.length) {
      return;
    }

    try {
      const users = await firstValueFrom(this.apiService.get<UserConfig[]>('users'));
      const userName = (uid?: string | null): string | null => {
        if (!uid) {
          return null;
        }
        const user = (users || []).find((item) => item.uid === uid);
        return user?.displayName || user?.email || uid;
      };
      this.selectedAttendanceJustification = {
        ...justification,
        submittedByName: userName(justification.submittedByUid),
        resolvedByName: userName(justification.resolvedByUid),
        history: (justification.history || []).map((item) => ({
          ...item,
          byName: userName(item.byUid),
        })),
      };
    } catch (error) {
      console.warn('Could not resolve attendance justification actors', error);
      this.selectedAttendanceJustification = {
        ...justification,
        submittedByName: justification.submittedByUid || null,
        resolvedByName: justification.resolvedByUid || null,
        history: (justification.history || []).map((item) => ({
          ...item,
          byName: item.byUid || null,
        })),
      };
    }
  }

  private getAttachmentBackendIndex(attachmentIndex: number): number {
    const storedIndex = Number(this.attachmentItems[attachmentIndex]?.['_backendIndex']);
    return Number.isInteger(storedIndex) && storedIndex >= 0 ? storedIndex : attachmentIndex;
  }

  private resolveAttachmentViewUrl(
    viewUrl: string | null | undefined,
    justificationId: string,
    attachmentIndex: number
  ): string {
    const fallback = `/attendance-justifications/${justificationId}/attachments/${attachmentIndex}`;
    const normalizedViewUrl = String(viewUrl || fallback).trim();
    try {
      return new URL(normalizedViewUrl, `${this.apiBaseUrl.replace(/\/+$/, '')}/`).toString();
    } catch {
      return `${this.apiBaseUrl.replace(/\/+$/, '')}/${normalizedViewUrl.replace(/^\/+/, '')}`;
    }
  }

  private async loadAttachmentPreviews(justificationId: string): Promise<void> {
    await Promise.all(this.attachmentItems.map(async (attachment, attachmentIndex) => {
      if (!String(attachment.contentType || '').startsWith('image/')) {
        return;
      }
      const backendIndex = this.getAttachmentBackendIndex(attachmentIndex);
      try {
        const viewUrl = this.resolveAttachmentViewUrl(attachment.viewUrl, justificationId, backendIndex);
        const response = await firstValueFrom(this.apiService.getBlob(viewUrl));
        const blob = response instanceof Blob ? response : response.body;
        if (!(blob instanceof Blob) || blob.size === 0) {
          return;
        }
        const objectUrl = URL.createObjectURL(blob);
        this.attachmentObjectUrls.add(objectUrl);
        this.attachmentPreviewUrls.set(backendIndex, objectUrl);
      } catch (error) {
        console.warn('Could not load attendance justification attachment preview', error);
      }
    }));
  }

  private releaseAttachmentPreviewUrls(): void {
    this.attachmentPreviewUrls.forEach((url) => {
      URL.revokeObjectURL(url);
      this.attachmentObjectUrls.delete(url);
    });
    this.attachmentPreviewUrls.clear();
  }

  formatEmployeeOptionLabel(employee: EmployeeRecord): string {
    return `${employee.fullName || employee.employeeNumber} · ${employee.employeeNumber}`;
  }

  private getEmployeeSearchText(value: string | EmployeeRecord | null | undefined): string {
    if (!value) {
      return '';
    }

    if (typeof value === 'string') {
      return value;
    }

    return this.formatEmployeeOptionLabel(value);
  }

  private formatDate(value?: string | null): string {
    if (!value) {
      return '-';
    }
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    const locale = this.translate.currentLang?.startsWith('en') ? 'en-US' : 'es-MX';
    return new Intl.DateTimeFormat(locale, { year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
  }

  private formatDateTime(value?: string | null): string {
    if (!value) {
      return '-';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    const locale = this.translate.currentLang?.startsWith('en') ? 'en-US' : 'es-MX';
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }

  private hasAccess(action: 'read' | 'create' | 'update' | 'delete'): boolean {
    return this.authorizationService.hasPermission('attendanceJustifications', action);
  }

  private showNotification(
    panelClass: string,
    message: string,
    verticalPosition: MatSnackBarVerticalPosition,
    horizontalPosition: MatSnackBarHorizontalPosition
  ): void {
    this.snackBar.open(message, undefined, {
      duration: 4000,
      panelClass,
      verticalPosition,
      horizontalPosition,
    });
  }
}
